import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../graphql/queries/getTmsTaskDetail';
import WaypointStepBar from './WaypointStepBar';
import { resolveSteps } from '../config/taskTabStepConfig';
import { TmsTaskWaypointStatusEnum } from 'enums/TmsEnum';

interface TaskWaypointDetailProps {
    waypoints: TmsTaskWaypointType[];
    onOpenLoadingDetailDrawer?: (waypointId: number) => void;
    onOpenUnloadingDetailDrawer?: (waypointId: number) => void;
    /** 可选，来自 GetTmsTaskDetail 的 task */
    task?: TmsTaskDetailType;
}

const TaskWaypointDetail: React.FC<TaskWaypointDetailProps> = ({
    waypoints,
    onOpenLoadingDetailDrawer,
    onOpenUnloadingDetailDrawer,
    task,
}) => {
    const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

    // 根据 routeType 过滤 waypoints：只展示有步骤配置的 tab
    const routeType = task?.routeType != null ? Number(task.routeType) : undefined;
    const displayWaypoints = useMemo(() => {
        if (!waypoints?.length) return [];
        const businessType = task?.businessType != null ? Number(task.businessType) : undefined;
        const simplified = task?.warehouseOperationSimplify === true;
        return waypoints.filter((w) => resolveSteps(w.waypointType, routeType, businessType, simplified).length > 0);
    }, [waypoints, routeType, task?.businessType, task?.warehouseOperationSimplify]);

    // 仅在「节点列表」变化时（如打开不同任务）重算默认 tab，数据刷新（如发车后 refetch）不自动切 tab
    const waypointIdsKey =
        displayWaypoints.length === 0 ? '' : displayWaypoints.map((w) => w.id).join(',');

    useEffect(() => {
        if (displayWaypoints.length === 0) {
            setActiveKey(undefined);
            return;
        }
        // 优先选择第一个「未完成」的节点作为当前 tab
        const firstNotDone = displayWaypoints.find((wp) => wp.waypointStatus !== TmsTaskWaypointStatusEnum.Done);
        setActiveKey((firstNotDone ?? displayWaypoints[0]).id);
    }, [waypointIdsKey]);

    const items = displayWaypoints.map((wp) => ({
        key: wp.id,
        label: (
            <span className="inline-block px-4 py-2 text-center font-semibold" style={{ minWidth: 70 }}>
                {wp.waypointSortingCode || '-'}
            </span>
        ),
        children:
            <WaypointStepBar
                task={task}
                waypoint={wp}
                waypoints={waypoints}
                onOpenLoadingDetailDrawer={onOpenLoadingDetailDrawer}
                onOpenUnloadingDetailDrawer={onOpenUnloadingDetailDrawer}
             />,
    }));

    return (
        <div className="bg-white mb-4">  
            <Tabs
                tabBarStyle={{ justifyContent: 'center' }}
                items={items}
                activeKey={activeKey}
                onChange={setActiveKey}
                destroyInactiveTabPane
            />
        </div>
    );
};

export default TaskWaypointDetail;